import { useEffect, useRef, useState } from "react";
import { DrawerForm, DrawerFormRef } from "../../../../components/drawers/DrawerForm";
import Icon from "../../../../components/base/Icon";
import { getUSA, hideLineWizard, refreshLinesList, useHasMaskedJobCalls, useHub } from "../../../../core/Global";
import Indicator from "../../../../components/base/Indicator";
import { API_ROUTES, JobCallbackTypes, RedirectTypes } from "../../../../core/Constants";
import { toastError, toastSuccess } from "../../../../components/base/Toast";
import { useNavigate } from "react-router-dom";
import Country from "../../../base/models/Country";
import DataItem from "../../../../models/DataItem";
import Select from "../../../../components/form/Select";
import { PhoneNumberTypes } from "../../../../core/Enums";
import TextBox from "../../../../components/form/TextBox";
import ToggleSwitch from "../../../../components/form/ToggleSwitch";
import TwilioLine from "../models/TwilioLine";
import NumberResults from "../../../../components/lines/NumberResults";
import NumberQuote from "../models/NumberQuote";
import PhoneInput from "../../../../components/form/PhoneInput";
import PhoneItem from "../../../../models/PhoneItem";
import Wallet from "../../../../models/Wallet";
import WalletChargeDrawer from "../../../system/settings/components/WalletChargeDrawer";
import "./LineSetupWizard.css";


const STEPS = [
    { n: 1, title: "Phone Number", hint: "Pick the number you want" },
    { n: 2, title: "Call Routing", hint: "Say where its calls go" },
    { n: 3, title: "Confirm", hint: "Check it over and add it" }
]


export default function LineSetupWizard() {

    const hub = useHub();
    const navigate = useNavigate();

    const drawerRef = useRef<DrawerFormRef>(null)

    // What the next number costs this customer. Plans include a few, so the price is not a fixed
    // figure any more: on Free the first one is included. Only the server knows how many they
    // already hold, so it is asked.
    const [quote, quoteSet] = useState<NumberQuote>()

    const [step, stepSet] = useState<number>(1);
    const [saving, savingSet] = useState<boolean>(false);

    // A paid number comes out of the wallet, so what is in the wallet decides whether the
    // confirm step can go ahead at all. Read when that step is reached, and again after the
    // customer tops it up.
    const [wallet, walletSet] = useState<Wallet>();
    const [chargeOpen, chargeOpenSet] = useState<boolean>(false);

    const balance = wallet?.balance ?? 0;
    const price = quote?.isFree ? 0 : (quote?.price ?? 0);
    const shortBy = price - balance;
    const canAfford = price <= 0 || balance >= price;

    // UpdateWallet rather than GetWallet: it also writes the balance into global state, so the
    // figure in the header agrees with the one on this step instead of going stale behind it.
    const fetchWallet = () => hub.UpdateWallet(w => walletSet(w));


    /* ---- step 1: finding a number ---------------------------------------------------------- */

    // The filters used to be read off the DOM by a nested form. A drawer is a form already, and a
    // form inside a form is not valid markup, so they are held as state instead.
    const [areaCode, areaCodeSet] = useState<string>("")
    const [keyword, keywordSet] = useState<string>("")
    const [wantsVoice, wantsVoiceSet] = useState<boolean>(true)
    const [wantsSms, wantsSmsSet] = useState<boolean>(false)
    const [wantsMms, wantsMmsSet] = useState<boolean>(false)

    const [numberTypes] = useState<DataItem[]>([
        { id: "1", name: "Local" },
        { id: "2", name: "Toll-Free" }
    ])
    const [selectedNumberType, selectedNumberTypeSet] = useState<DataItem>({ id: "1", name: "Local" });

    const [countries, countriesSet] = useState<Country[]>();
    const [selectedCountry, selectedCountrySet] = useState<Country>();
    const fetchCountries = () => {
        hub.Get(API_ROUTES.CountriesAllActive, res => {
            if (res.data) {
                countriesSet(res.data);
                selectedCountrySet(res.data[0]);
            }
        })
    }

    const [states, statesSet] = useState<any[]>();
    const [areaCodes, areaCodesSet] = useState<any[]>();
    const fetchStates = () => {
        hub.Get("/base/states/?countryId=" + selectedCountry?.id, res => {
            if (res.data) statesSet(res.data);
        })
    }

    useEffect(() => {
        if (states && selectedCountry?.alpha2Code?.toLowerCase() != "us") {
            var arr: any[] = [];
            states.forEach(s => {
                s.areaCodes.forEach((code: any) => {
                    if (arr.indexOf(code) < 0) arr.push(code);
                });
            });
            areaCodesSet(arr);
        }
    }, [states])

    useEffect(() => {
        if (selectedCountry) fetchStates();
    }, [selectedCountry])

    // US and Canada area codes are worth grouping by state, and there are hundreds of them; every
    // other country has a short flat list. Select takes one flat source either way, with the
    // state carried on each item as the group.
    const alpha2 = selectedCountry?.alpha2Code?.toLowerCase();
    const groupedAreaCodes = alpha2 == "us" || alpha2 == "ca";

    const areaCodeItems = groupedAreaCodes
        ? (states ?? []).flatMap((st: any) =>
            (st.areaCodes ?? []).map((code: any) => ({ id: String(code), name: String(code), group: st.name })))
        : (areaCodes ?? []).slice().sort((a: any, b: any) => a - b).map((code: any) =>
            ({ id: String(code), name: "+" + selectedCountry?.phoneCode + " (" + code + ")" }));

    const [fetchingLines, fetchingLinesSet] = useState<boolean>();
    const [fetchedLines, fetchedLinesSet] = useState<boolean>();
    const [lines, linesSet] = useState<TwilioLine[]>();
    const [selectedLine, selectedLineSet] = useState<TwilioLine>();

    const fetchLines = () => {

        fetchedLinesSet(false);
        fetchingLinesSet(true);
        selectedLineSet(undefined);

        const q = new URLSearchParams();

        if (selectedCountry?.id) q.set("country", selectedCountry.id);
        if (areaCode) q.set("areaCode", areaCode);
        if (keyword) q.set("keyword", keyword);
        if (wantsVoice) q.set("voice", "True");
        if (wantsSms) q.set("sms", "True");
        if (wantsMms) q.set("mms", "True");
        if (selectedNumberType?.id) q.set("numberType", selectedNumberType.id);

        // As many as the endpoint will give. Twenty-one was barely two rows of cards and most
        // searches were refreshed two or three times before anything looked right.
        q.set("limit", "50");

        // Same reason the confirm step passes one: without an error handler a failed search
        // leaves the spinner turning with nothing behind it.
        hub.Get("/voice/lines/available/?" + q.toString(),
            res => {
                linesSet(res.data ?? []);
                fetchedLinesSet(true);
                fetchingLinesSet(false);
            },
            () => {
                linesSet([]);
                fetchedLinesSet(true);
                fetchingLinesSet(false);
                toastError("Could not search for numbers. Please try again.");
            })

    }


    /* ---- step 2: what the number is for ---------------------------------------------------- */

    // The same questions the settings form asks after the fact, under the same names, so what is
    // collected here is exactly what that endpoint already knows how to save. Asking now means a
    // number is never bought without anyone knowing what it is for.
    const [inboundItems] = useState<DataItem[]>([
        { id: "0", name: "None" },
        { id: "1", name: "Extensions" },
        { id: "2", name: "Queues" },
        { id: "3", name: "Redirect" }
    ])

    // Same gate as the settings drawer: no add-on, no job-customers redirect to choose.
    const maskedAllowed = useHasMaskedJobCalls();
    const redirectItems = maskedAllowed
        ? RedirectTypes
        : RedirectTypes.filter((r: any) => r.id !== "1");

    const [sourceId, sourceIdSet] = useState<string>();
    const [inboundDest, inboundDestSet] = useState<string>();
    const [selectedExt, selectedExtSet] = useState<any>();
    const [selectedQue, selectedQueSet] = useState<any>();
    const [spamProtection, spamProtectionSet] = useState<boolean>(false);
    const [redirectType, redirectTypeSet] = useState<string>();
    const [callbackPriority, callbackPrioritySet] = useState<string>("1");
    const [operatorPhone, operatorPhoneSet] = useState<PhoneItem>();
    const [redirectPhone, redirectPhoneSet] = useState<PhoneItem>();

    const [sources, sourcesSet] = useState<any[]>();
    const [exts, extsSet] = useState<any[]>();
    const [ques, quesSet] = useState<any[]>();

    const fetchSources = () => hub.Get(API_ROUTES.TagifyJobSources, res => { if (res.data) sourcesSet(res.data) })
    const fetchExts = () => hub.Get(API_ROUTES.TagifyExtensions, res => { if (res.data) extsSet(res.data) })
    const fetchQueus = () => hub.Get(API_ROUTES.TagifyQueues, res => { if (res.data) quesSet(res.data) })

    // Changing where calls go makes whatever was chosen under the old destination meaningless.
    useEffect(() => {
        selectedExtSet(undefined);
        selectedQueSet(undefined);
        redirectTypeSet(undefined);
        operatorPhoneSet(undefined);
        redirectPhoneSet(undefined);
    }, [inboundDest])


    /* ---- moving between the steps ---------------------------------------------------------- */

    // Nothing on step two has to be answered. Leaving the source blank means the number has no
    // source; leaving the destination blank means nobody answers it yet. Both are things a
    // customer can come back and set from the number's own settings, and neither is worth
    // holding up a purchase for.
    //
    // Only the number itself is required, because there is nothing to buy without one.
    const blocking = (): string | null => {
        if (step == 1 && !selectedLine) return "Choose a phone number to continue.";
        return null;
    }

    // The destination as it will actually be saved.
    //
    // A half-answered destination is treated as no destination: choosing Extensions and then not
    // choosing an extension is somebody who changed their mind, and sending it as-is would have
    // the routing rejected and the number left unrouted after it had already been paid for.
    const effectiveDest = (): string => {
        if (inboundDest == "1") return selectedExt ? "1" : "0";
        if (inboundDest == "2") return selectedQue ? "2" : "0";
        if (inboundDest == "3") {
            if (!redirectType) return "0";
            if (redirectType == "2" && (!redirectPhone?.phone || redirectPhone.phone.length < 8)) return "0";
            return "3";
        }
        return "0";
    }

    const next = () => {
        const problem = blocking();
        if (problem) { toastError(problem); return; }
        stepSet(step + 1);
    }


    /* ---- step 3: everything happens here --------------------------------------------------- */

    const routingBody = () => {

        const dest = effectiveDest();

        const body: any = {
            sourceId: sourceId,
            inboundDest: dest,
            spamProtection: spamProtection
        };

        if (dest == "1") body.inboundExtension = selectedExt?.value;
        if (dest == "2") body.inboundQueue = selectedQue?.value;

        if (dest == "3") {
            body.inboundRedirect = redirectType;

            if (redirectType == "1") {
                body.jobRedirectCallBackPriority = callbackPriority;
                if (operatorPhone?.phone) {
                    body.jobCallbackExternalPhoneNumber = operatorPhone.phone;
                    body.jobCallbackExternalPhoneNumberCountryCode = operatorPhone.countryCode ?? "+1";
                }
            }

            if (redirectType == "2") {
                body.redirectToExternalNumber = redirectPhone?.phone;
                body.redirectToExternalNumberCountryCode = redirectPhone?.countryCode ?? "+1";
            }
        }

        return body;
    }

    // Is there anything to save after the number is bought?
    //
    // A number arrives with no source and answering nobody, which is exactly what leaving both
    // questions blank asks for. Sending that anyway would still call out to the PBX to set a
    // routing the line already has.
    const hasRoutingToSave = () => !!sourceId || effectiveDest() != "0";

    // Nothing is bought or saved until this runs.
    //
    // The purchase and the routing are two calls, and only the first is worth waiting for. The
    // routing has to reach the PBX, which on a bad day takes twenty seconds or more to answer or
    // to time out -- and by then the number is already bought and paid for. Holding the drawer
    // shut over that makes a finished purchase look like a hung one, which is what it did.
    //
    // So the drawer closes as soon as the number exists, and the routing follows on its own. If
    // it fails the customer is told, and the number is still theirs to open and finish by hand.
    //
    // Both calls pass an error handler as well as a success one: Hub only calls back on success,
    // so without it a request that throws never returns to the button that is waiting on it.
    const confirm = () => {

        if (!selectedLine) return;

        // A wallet that cannot cover the number is not an error worth turning the customer away
        // with: it is a step they have not taken yet. The top-up drawer opens over this one, and
        // when it closes they are back here with the number and the routing still chosen.
        if (!canAfford) { chargeOpenSet(true); return; }

        savingSet(true);

        hub.Post(API_ROUTES.Lines + "wizard", {
            phoneNumber: selectedLine.phoneNumber,
            numberType: selectedNumberType.id,
            friendlyName: selectedLine.friendlyName
        },
            res => {

                if (!res.success) {
                    savingSet(false);
                    toastError(res.message ?? "The number could not be added. Please try again.");
                    return;
                }

                const lineId = res.data?.id;
                const routing = hasRoutingToSave();

                savingSet(false);
                toastSuccess(quote?.isFree
                    ? "The number has been added to your account."
                    : "The number has been bought and added to your account.");

                refreshLinesList();
                navigate("/voice/lines");
                drawerRef.current?.close();

                if (!lineId || !routing) return;

                // Left running after the drawer is gone. Nothing here touches this component --
                // it is unmounted by now -- so it only refreshes the list or says what went wrong.
                const routingFailed = () => toastError(
                    "The number is yours, but its call routing was not saved. Open its settings to finish.");

                hub.Put("/voice/lines/inbound/" + lineId, routingBody(),
                    r => { if (r.success) refreshLinesList(); else routingFailed(); },
                    () => routingFailed());
            },
            () => {
                savingSet(false);
                toastError("The number could not be added. Please try again.");
            });
    }

    // What was chosen in step 2, in the words it was chosen with.
    // Read off the destination as it will be saved, not as it was clicked, so the customer is
    // shown what is really about to happen.
    const routingSummary = () => {

        const dest = effectiveDest();

        if (dest == "1") return "Extension " + (selectedExt?.title ?? "");
        if (dest == "2") return "Queue " + (selectedQue?.title ?? "");
        if (dest == "3") {
            const t = RedirectTypes.find((r: any) => r.id == redirectType)?.name ?? "Redirect";
            if (redirectType == "2") return t + " — " + (redirectPhone?.phoneFriendly ?? "");
            return t;
        }

        return "Nobody yet — you can set this later";
    }


    const fetchQuote = () => {
        hub.Get(API_ROUTES.Lines + "numberquote?numberType=" + (selectedNumberType?.id ?? "1"), res => {
            if (res.success) quoteSet(res.data)
        })
    }

    // The drawer used to be a modal and was set up in its open hook. The component is only
    // mounted while the wizard is showing, so mounting is that same moment.
    useEffect(() => {
        stepSet(1);
        fetchQuote();
    }, [])

    // A toll-free number is priced differently from a local one, so the quote is asked again
    // whenever the kind being bought changes.
    useEffect(() => {
        fetchQuote();
    }, [selectedNumberType])

    useEffect(() => {
        if (step == 1) {
            fetchLines();
            fetchCountries();
        }
        if (step == 2) {
            fetchSources();
            fetchExts();
            fetchQueus();
        }
        if (step == 3) {
            fetchWallet();
        }
    }, [step])


    // Where the customer is, on its own row in the drawer's header. In the body it scrolled away
    // with the list of numbers, which is the one place a wizard's progress has to stay put.
    const WizardSteps = () => (
        <div className="line-wizard-steps">
            {
                STEPS.map(s =>
                    <div key={s.n}
                        className={"line-wizard-step"
                            + (step == s.n ? " is-current" : "")
                            + (step > s.n ? " is-done" : "")}>
                        <div className="line-wizard-step-mark">
                            {step > s.n ? <Icon icon="check" type="solid" size={12} /> : s.n}
                        </div>
                        <div className="line-wizard-step-text">
                            <div className="line-wizard-step-title">{s.title}</div>
                            <div className="line-wizard-step-hint">{s.hint}</div>
                        </div>
                    </div>
                )
            }
        </div>
    )

    // What moves the wizard along, in the header's top-right corner where the drawer's close
    // button used to be. Cancel is the way out now, so there is no second control that also
    // closes the drawer (hideClose).
    const WizardActions = () => (
        <div className="line-wizard-actions">
            {
                step > 1 &&
                <button type="button" className="btn btn-sm btn-light"
                    disabled={saving}
                    onClick={e => { e.preventDefault(); stepSet(step - 1); }}>
                    <Icon icon="arrow-left" type="solid" size={12} className="me-2" />
                    Back
                </button>
            }

            <button type="button" className="btn btn-sm btn-secondary"
                disabled={saving}
                onClick={e => { e.preventDefault(); drawerRef.current?.close(); }}>
                Cancel
            </button>

            {
                step < 3 ?
                    <button type="button" className="btn btn-sm btn-primary"
                        onClick={e => { e.preventDefault(); next(); }}>
                        Continue
                        <Icon icon="arrow-right" type="solid" size={12} className="ms-2" />
                    </button>
                    :
                    <button type="button" className="btn btn-sm btn-primary"
                        disabled={saving}
                        onClick={e => { e.preventDefault(); confirm(); }}>
                        {
                            saving ?
                                <>
                                    <Icon icon="spinner-third" spin />
                                    <span className="ms-2">Please wait...</span>
                                </>
                                : !canAfford ?
                                    <>
                                        {/* The button says what it will actually do. Pressing
                                            it opens the top-up drawer, not a purchase. */}
                                        <Icon icon="wallet" type="solid" size={12} className="me-2" />
                                        Add Balance to Continue
                                    </>
                                    :
                                    <>
                                        <Icon icon="check" type="solid" size={12} className="me-2" />
                                        {quote?.isFree ? "Confirm & Add Number" : "Confirm & Buy Number"}
                                    </>
                        }
                    </button>
            }
        </div>
    )


    return (
        <DrawerForm ref={drawerRef} id="drawer-line-setup-wizard"
            title={quote?.isFree ? "Add Phone Number" : "Buy Phone Number"}
            titleIcon="phone-rotary"
            hideButtons
            titleComponent={WizardSteps}
            titleComponentBelow
            toolbarComponent={WizardActions}
            toolbarCenter
            hideClose
            onClose={() => hideLineWizard()}
            size="xlarge">

            <div className="line-wizard">

                <div className="line-wizard-body">

                    {
                        step == 1 &&
                        <>
                            <div className="d-flex align-items-center flex-wrap gap-2 border-bottom pb-3 mb-4">
                                <div className="w-100px">
                                    <Select id="drp-countries" small block marginBottom={0} deselect={false}
                                        placeholder="Country" source={countries} dataText="alpha2Code" dataValue="id"
                                        onSelectItem={c => selectedCountrySet(c)} defaultValue={getUSA().id} />
                                </div>
                                <div className="w-150px">
                                    <Select id="drp-areacodes" small block marginBottom={0}
                                        placeholder="Area Codes" source={areaCodeItems}
                                        dataGroup={groupedAreaCodes ? "group" : undefined}
                                        value={areaCode}
                                        onSelect={v => areaCodeSet(v ?? "")}
                                        onClear={() => areaCodeSet("")} />
                                </div>
                                {/* numeric keeps it to digits, the way the raw input did. Enter is
                                    caught on the wrapper: TextBox has no key handler of its own to
                                    pass, and the drawer form would otherwise take Enter as a submit. */}
                                <div className="w-125px"
                                    onKeyDown={e => {
                                        if (e.key == "Enter") {
                                            e.preventDefault();
                                            fetchLines();
                                        }
                                    }}>
                                    <TextBox id="txt-lines-keyword" small block marginBottom={0} numeric
                                        placeholder="Part of Number" maxLength={8} value={keyword}
                                        onTextChanges={v => keywordSet(v ?? "")} />
                                </div>
                                <div className="w-150px">
                                    <Select id="drp-numbertypes" small block marginBottom={0} deselect={false}
                                        source={numberTypes}
                                        onSelectItem={c => selectedNumberTypeSet(c)} defaultValue="1" />
                                </div>
                                <div className="ms-2">
                                    <div className="form-check form-check-custom form-check-solid form-check-sm">
                                        <input id="chk-search-voice" className="form-check-input" type="checkbox"
                                            checked={wantsVoice} onChange={e => wantsVoiceSet(e.target.checked)} />
                                        <label className="form-check-label" htmlFor="chk-search-voice">
                                            Voice
                                        </label>
                                    </div>
                                </div>
                                <div className="ms-2">
                                    <div className="form-check form-check-custom form-check-solid form-check-sm">
                                        <input id="chk-search-sms" className="form-check-input" type="checkbox"
                                            checked={wantsSms} onChange={e => wantsSmsSet(e.target.checked)} />
                                        <label className="form-check-label" htmlFor="chk-search-sms">
                                            SMS
                                        </label>
                                    </div>
                                </div>
                                <div className="ms-2">
                                    <div className="form-check form-check-custom form-check-solid form-check-sm">
                                        <input id="chk-search-mms" className="form-check-input" type="checkbox"
                                            checked={wantsMms} onChange={e => wantsMmsSet(e.target.checked)} />
                                        <label className="form-check-label" htmlFor="chk-search-mms">
                                            MMS
                                        </label>
                                    </div>
                                </div>
                                {/* type="button": the drawer is itself a form, and a bare button
                                    inside one submits it. */}
                                <button type="button" className="btn btn-primary btn-sm w-125px ms-auto"
                                    onClick={e => { e.preventDefault(); fetchLines(); }}>
                                    <Icon icon="refresh" />
                                    Refresh List
                                </button>
                            </div>

                            {
                                fetchingLines &&
                                <div className="py-20">
                                    <Indicator />
                                </div>
                            }

                            {
                                fetchedLines &&
                                <NumberResults
                                    lines={lines}
                                    selected={selectedLine}
                                    onSelect={selectedLineSet} />
                            }
                        </>
                    }

                    {
                        step == 2 &&
                        <div className="line-wizard-form">

                            <div className="line-wizard-chosen">
                                <Icon icon="phone-rotary" type="solid" size={16} />
                                <span className="line-wizard-chosen-number">{selectedLine?.friendlyName}</span>
                                <span className="line-wizard-chosen-note">is the number being set up</span>
                            </div>

                            <Select label="Source" id="drp-wizard-source" labelUp block
                                placeholder="Where this number is advertised"
                                source={sources} dataText="title" dataValue="value"
                                onSelect={v => sourceIdSet(v)}
                                onClear={() => sourceIdSet(undefined)} />

                            <Select label="Destination" id="drp-wizard-destination" labelUp block
                                placeholder="Who answers calls to this number"
                                source={inboundItems}
                                onSelect={v => inboundDestSet(v)}
                                onClear={() => inboundDestSet(undefined)} />

                            {
                                inboundDest == "1" &&
                                <Select label="Extension" id="drp-wizard-extension" labelUp block
                                    placeholder="Choose an extension"
                                    source={exts} dataText="title" dataValue="value"
                                    onSelectItem={e => selectedExtSet(e)} />
                            }

                            {
                                inboundDest == "2" &&
                                <Select label="Queue" id="drp-wizard-queue" labelUp block
                                    placeholder="Choose a queue"
                                    source={ques} dataText="title" dataValue="value"
                                    onSelectItem={e => selectedQueSet(e)} />
                            }

                            {
                                (inboundDest == "1" || inboundDest == "2") &&
                                <div className="mb-8">
                                    <ToggleSwitch label="Spam Protection (Press 1 to Continue)" raw small
                                        checked={spamProtection} onChange={e => spamProtectionSet(e)} />
                                    <div className="text-muted fs-8 mt-2">
                                        Callers must press 1 before the call is connected, which blocks most
                                        robocalls and spam.
                                    </div>
                                </div>
                            }

                            {
                                inboundDest == "3" &&
                                <Select label="Redirect To" id="drp-wizard-redirect" labelUp block
                                    placeholder="Choose where to send the call"
                                    source={redirectItems}
                                    onSelect={v => redirectTypeSet(v)}
                                    onClear={() => redirectTypeSet(undefined)} />
                            }

                            {
                                inboundDest == "3" && redirectType == "1" &&
                                <>
                                    <Select label="Callback Priority" id="drp-wizard-callback" labelUp block
                                        source={JobCallbackTypes} deselect={false} defaultValue="1"
                                        onSelect={v => callbackPrioritySet(v ?? "1")} />

                                    <PhoneInput id="phone-wizard-operator" label="Operator Number" labelUp block
                                        countryCodes="us-only"
                                        onComplete={e => operatorPhoneSet(e)}
                                        onInComplete={() => operatorPhoneSet(undefined)} />
                                </>
                            }

                            {
                                inboundDest == "3" && redirectType == "2" &&
                                <PhoneInput id="phone-wizard-redirect" label="Redirect To" labelUp block
                                    countryCodes="us-only"
                                    onComplete={e => redirectPhoneSet(e)}
                                    onInComplete={() => redirectPhoneSet(undefined)} />
                            }

                        </div>
                    }

                    {
                        step == 3 &&
                        <div className="line-wizard-form is-wide">
                            <div className="line-confirm">

                                <div className="line-confirm-head">
                                    <div className="line-confirm-icon">
                                        <Icon icon="phone-rotary" type="solid" size={22} />
                                    </div>
                                    <div className="line-confirm-number">{selectedLine?.friendlyName}</div>
                                    {
                                        selectedLine?.region &&
                                        <div className="line-confirm-place">
                                            <Icon icon="location-dot" size={12} />
                                            {selectedLine.rateCenter && selectedLine.rateCenter != ""
                                                ? selectedLine.rateCenter + ", " + selectedLine.region
                                                : selectedLine.region}
                                        </div>
                                    }
                                </div>

                                <div className="line-confirm-rows">
                                    <div className="line-confirm-row">
                                        <span className="line-confirm-label">Type</span>
                                        <span className="line-confirm-value">
                                            {PhoneNumberTypes[selectedLine?.numberType ?? 1]}
                                        </span>
                                    </div>
                                    <div className="line-confirm-row">
                                        <span className="line-confirm-label">Calls go to</span>
                                        <span className="line-confirm-value">{routingSummary()}</span>
                                    </div>
                                    {
                                        /* Only where it applies, and only where the destination
                                           survived: spam protection means nothing on a number
                                           nobody answers. */
                                        (effectiveDest() == "1" || effectiveDest() == "2") &&
                                        <div className="line-confirm-row">
                                            <span className="line-confirm-label">Spam protection</span>
                                            <span className="line-confirm-value">{spamProtection ? "On" : "Off"}</span>
                                        </div>
                                    }
                                </div>

                                {/* The price is the point of this step, so it is the loudest thing on
                                    it, and on a plan that includes the number it says so rather than
                                    showing a figure the customer will not be charged. */}
                                <div className={"line-confirm-price" + (quote?.isFree ? " is-free" : "")}>
                                    <div className="line-confirm-price-label">
                                        {quote?.isFree ? "Included in your plan" : "You will be charged"}
                                    </div>
                                    <div className="line-confirm-price-value">{quote?.priceText}</div>
                                    {
                                        quote?.note &&
                                        <div className="line-confirm-price-note">{quote.note}</div>
                                    }
                                </div>

                                {
                                    /* Only when the wallet cannot cover it. A wallet with enough in
                                       it is not news: the customer is about to be told the money
                                       comes from there by the line below, and a row confirming
                                       there is enough is one more thing to read past. */
                                    !quote?.isFree && !canAfford &&
                                    <div className="line-confirm-wallet is-short">
                                        <Icon icon="triangle-exclamation" type="solid" size={15} />
                                        <div className="line-confirm-wallet-text">
                                            Your wallet holds ${balance.toFixed(2)}, which is
                                            ${shortBy.toFixed(2)} short. Add some balance and the number
                                            is yours — nothing here is lost.
                                        </div>
                                        <button type="button" className="btn btn-sm btn-light-primary ms-auto"
                                            onClick={e => { e.preventDefault(); chargeOpenSet(true); }}>
                                            <Icon icon="plus" type="solid" size={11} className="me-2" />
                                            Add Balance
                                        </button>
                                    </div>
                                }

                                {
                                    !quote?.isFree &&
                                    <div className="line-confirm-fineprint">
                                        <Icon icon="circle-info" type="solid" size={13} className="me-2" />
                                        Taken from your wallet now, and again each month for as long as you
                                        keep the number. Calls and messages on it are charged separately as
                                        you use them.
                                    </div>
                                }

                            </div>
                        </div>
                    }

                </div>

            </div>

            {
                /* Opened over this drawer rather than instead of it: Drawer stacks whatever opens
                   on top and slides what is underneath aside, so the number and the routing are
                   still chosen and still visible when the customer comes back to them.

                   The balance is read again on close, not only on save, because a new card goes
                   out to Stripe Checkout and the money may have arrived by another route. */
                chargeOpen &&
                <WalletChargeDrawer
                    onClose={() => { chargeOpenSet(false); fetchWallet(); }}
                    onSaved={() => fetchWallet()} />
            }

        </DrawerForm>
    )
}
